import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { FiChevronLeft, FiMessageSquare } from 'react-icons/fi'
import { faPlain, longJalali } from 'utilities/helper/format'

/**
 * The way back from a granted loan to the درخواست it was granted from.
 *
 * A loan request stops being a request the moment it is accepted, but its
 * thread does not stop mattering: «چک تضمین شمارهٔ ۱۲۳۴۵۶ گرفته شد» was
 * written there by the manager while deciding, and so was the member's answer
 * about their ضامن. Until this the only door left to that thread was the
 * «پذیرفته‌شده» tab of وام‌های درخواستی, and nobody goes there — they go to
 * the loan. So the loan carries the door.
 *
 * Rendered only when the server sends `loan_demand`, which it does for the
 * borrower and the fund's managers and for nobody else: `can_show_others_loans`
 * lets an ordinary member open this page, and the کارتابل behind the link is
 * not theirs to read. The decision is the server's precisely so this component
 * never has to be trusted with it.
 */
export default function DemandLink({
    demand,
    fundId,
}: {
    demand: ILoanOriginDemand
    fundId?: string
}) {
    const navigate = useNavigate()

    const open = () => {
        posthog.capture('loan_demand_cartable_opened', {
            fund_id: fundId,
            demand_id: demand.id,
            source: 'loan_detail',
            comments_count: demand.comments_count,
        })
        navigate(`/fund/loan-request/detail/${demand.id}?fund=${fundId}`)
    }

    // The count is the reason to tap, so it leads. With nothing written yet
    // the row still earns its place — the decision trail is on the other side
    // — but it should not promise messages that are not there.
    const subtitle = demand.comments_count
        ? `${faPlain.format(demand.comments_count)} پیام و سوابق بررسی آن`
        : 'گفت‌وگو و سوابق بررسی این درخواست'

    return (
        <div className="px-4 pt-3">
            <button
                type="button"
                onClick={open}
                className="flex w-full items-center gap-3 rounded-card border border-divider bg-white p-4 text-right shadow-card active:scale-[0.99] transition-transform motion-reduce:transition-none"
            >
                <span
                    aria-hidden
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand-deep"
                >
                    <FiMessageSquare className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-heading">
                        درخواستی که این وام از آن داده شد
                    </span>
                    <span className="mt-[2px] block truncate text-[11px] text-text-secondary">
                        {subtitle}
                    </span>
                    <span className="mt-[2px] block text-[11px] text-text-muted">
                        ثبت درخواست: {longJalali(demand.created)}
                    </span>
                </span>
                <FiChevronLeft
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-text-muted"
                />
            </button>
        </div>
    )
}
